import { HEADER_H, LINE_H, MARGIN_X, PAGE_H, PAGE_W, ROWS, rowBandY } from 'shared';
import type { Paper } from 'shared';

export type RowMarkKind = 'new' | 'task' | 'event' | 'note' | 'ignored' | 'changed';

const MARK_COLOR: Record<RowMarkKind, string> = {
  new: '#1F47C9',
  task: '#1F8A4C',
  event: '#1F47C9',
  note: '#9AA3AE',
  ignored: '#C4CAD2',
  changed: '#B7791F',
};

export interface PaperStyle {
  paper: Paper;
  marks: Map<number, RowMarkKind>;
  highlightRows: number[];
}

/**
 * O papel é SEMPRE claro, mesmo com a interface no escuro (SPEC §6.3):
 * a imagem que vai para a IA sai daqui, e tinta clara sobre fundo escuro
 * seria ilegível para a leitura.
 */
export function drawPaper(ctx: CanvasRenderingContext2D, style: PaperStyle): void {
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, PAGE_W, PAGE_H);

  ctx.lineWidth = 1;
  if (style.paper === 'ruled') {
    ctx.strokeStyle = '#DCE3EC';
    ctx.beginPath();
    for (let r = 0; r <= ROWS; r++) {
      const y = HEADER_H + r * LINE_H;
      ctx.moveTo(0, y);
      ctx.lineTo(PAGE_W, y);
    }
    ctx.stroke();
  } else if (style.paper === 'grid') {
    ctx.strokeStyle = '#E6EBF2';
    ctx.beginPath();
    for (let x = 0; x <= PAGE_W; x += LINE_H / 2) { ctx.moveTo(x, 0); ctx.lineTo(x, PAGE_H); }
    for (let y = 0; y <= PAGE_H; y += LINE_H / 2) { ctx.moveTo(0, y); ctx.lineTo(PAGE_W, y); }
    ctx.stroke();
  } else if (style.paper === 'dotted') {
    ctx.fillStyle = '#D5DCE6';
    for (let x = LINE_H / 2; x < PAGE_W; x += LINE_H / 2) {
      for (let y = HEADER_H; y < PAGE_H; y += LINE_H / 2) {
        ctx.beginPath();
        ctx.arc(x, y, 1.4, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  if (style.paper !== 'blank') {
    ctx.strokeStyle = '#F0C9C4';
    ctx.beginPath();
    ctx.moveTo(MARGIN_X, 0);
    ctx.lineTo(MARGIN_X, PAGE_H);
    ctx.stroke();

    ctx.strokeStyle = '#C9D3E0';
    ctx.beginPath();
    ctx.moveTo(0, HEADER_H);
    ctx.lineTo(PAGE_W, HEADER_H);
    ctx.stroke();
  }

  for (const row of style.highlightRows) {
    const [top, bottom] = rowBandY(row);
    ctx.fillStyle = 'rgba(31, 71, 201, 0.10)';
    ctx.fillRect(0, top, PAGE_W, bottom - top);
  }

  // Marcas na margem esquerda (SPEC §6.3).
  for (const [row, kind] of style.marks) {
    const [top, bottom] = rowBandY(row);
    const cy = (top + bottom) / 2;
    ctx.fillStyle = MARK_COLOR[kind];
    if (kind === 'new') {
      ctx.fillRect(18, top + 6, 4, bottom - top - 12);
    } else if (kind === 'note') {
      ctx.beginPath();
      ctx.arc(20, cy, 3.5, 0, Math.PI * 2);
      ctx.fill();
    } else if (kind === 'ignored') {
      ctx.fillRect(14, cy - 1.5, 14, 3);
    } else {
      ctx.font = '600 22px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(kind === 'task' ? '✓' : kind === 'event' ? '▣' : '⚠', 20, cy);
    }
  }
}

/** Número da linha na margem, azul, só na imagem que vai para a IA. */
export function drawRowNumbers(ctx: CanvasRenderingContext2D, rows: number[]): void {
  ctx.save();
  ctx.fillStyle = '#1F47C9';
  ctx.font = '600 20px system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  for (const row of rows) {
    const [top, bottom] = rowBandY(row);
    ctx.fillText(`L${row}`, 8, (top + bottom) / 2);
  }
  ctx.restore();
}
